import { addDays } from 'date-fns';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../components/forms/CustomSelect';
import { DatePicker } from '../../../components/forms/DatePicker';
import RequiredInput from '../../../components/forms/InputHOC';
import ModalForm from '../../../components/forms/ModalForm';
import useUsers from '../../../hooks/api-calls/useUsers';
import { PickupService } from '../../../services/pickup/pickupService';
import { Pickup } from '../../../type/entities/pickup-type';
import { pickupStatuses } from '../../../utils/SelectorMapping';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import useHandleErrors from '../../../hooks/useHandleErrors';

/**
 * UTILIDADES DE FORMATO DE FECHA
 * Funciones reutilizables para manejo consistente de fechas
 */

// Constantes para formatos de fecha
const DATE_FORMATS = {
    DISPLAY: 'DD/MM/YY HH:mm:ss',        // Formato mostrado al usuario
    BACKEND: 'YYYY-MM-DD HH:mm:ss',      // Formato para envío al backend (Y-m-d H:i:s)
    INTERNAL: 'YYYY-MM-DD HH:mm:ss',     // Formato interno de moment.js
    VALIDATION: 'YYYY-MM-DD HH:mm:ss'    // Formato para validación
} as const;

/**
 * Convierte una fecha del formato interno al formato de display
 * @param dateString - Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 * @returns Fecha en formato display (DD/MM/YY HH:MM:SS)
 */
const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    return momentDate.isValid() ? momentDate.format(DATE_FORMATS.DISPLAY) : '';
};

/**
 * Convierte una fecha del formato interno al formato del backend
 * @param dateString - Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 * @returns Fecha en formato backend (Y-m-d H:i:s)
 */
const formatDateForBackend = (dateString: string): string => {
    if (!dateString) return '';
    const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    return momentDate.isValid() ? momentDate.format(DATE_FORMATS.BACKEND) : '';
};

/**
 * Convierte una fecha de Date object a formato interno
 * @param date - Date object
 * @returns Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 */
const formatDateFromPicker = (date: Date | null): string => {
    if (!date) return '';
    return moment(date).format(DATE_FORMATS.INTERNAL);
};

/**
 * Valida que una fecha esté en el formato correcto
 * @param dateString - Fecha a validar
 * @returns true si la fecha es válida
 */
const validateDateFormat = (dateString: string): boolean => {
    if (!dateString) return false; // La fecha es requerida en este modal
    const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    return momentDate.isValid() && momentDate.isAfter(moment()); // Debe ser fecha futura
};

interface FailPickupModalProps {
	isOpen: boolean;
	onClose: () => void;
	pickup: Pickup;
}


const validationSchema = yup.object({
	failureReason: yup
		.string()
		.trim()
		.required('El motivo del fallo es obligatorio')
		.max(1000, 'El motivo no puede exceder los 1000 caracteres'),
	failureExplanation: yup
		.string()
		.trim()
		.required('La explicación del fallo es obligatoria')
		.max(1000, 'La explicación no puede exceder los 1000 caracteres'),
	newDate: yup
		.string()
		.required('La nueva fecha es obligatoria')
		.test('valid-format', 'El formato de fecha no es válido o la fecha debe ser futura', (val) => {
			return validateDateFormat(val || '');
		}),
	userId: yup.string().notRequired(),
});

const FailPickupModal: React.FC<FailPickupModalProps> = ({ isOpen, onClose, pickup }) => {

	// STATES

	const [loading, setLoading] = useState(false);
	const [isSearched, setIsSearched] = useState(false);

	// HOOKS

    const { userCan } = usePrivilege();
	const { getCompaniesList } = useCompanies();
	const { handleErrors } = useHandleErrors();

	// SERVICES

	const pickupService = new PickupService();

	// USERS (opcional)

	const { getUsersList, usersLoading } = useUsers();

	// FORM
	const formik = useFormik({
		initialValues: {
			failureReason: '',
			failureExplanation: '',
			// Sin fecha por defecto: el usuario debe seleccionar manualmente
			newDate: '',
			userId: '',
			company: ''
		},
		validationSchema,
		onSubmit: async (values) => handleSubmit(values),
	});

	// FUNCTIONS
	//-------------------------------------------------------------------------------------------------------------
	/**
	 * @ES MARCAR RECOGIDA COMO FALLIDA
	 * @EN MARK PICKUP AS FAILED
	 */
	//-------------------------------------------------------------------------------------------------------------
	const handleSubmit = async (values: any) => {
		setLoading(true);

		// Convertir fecha al formato del backend
		const backendFormattedDate = formatDateForBackend(values.newDate);
		
		// Validar formato antes del envío
		if (!validateDateFormat(values.newDate)) {
			toast.error('El formato de fecha no es válido o la fecha debe ser futura');
			setLoading(false);
			return;
		}

		const valuesToFail = {
			id: pickup?.id,
			failureReason: values.failureReason,
			failureExplanation: values.failureExplanation,
			newDate: backendFormattedDate, // Fecha en formato Y-m-d H:i:s para el backend
			userId: values.userId || undefined,
			company: values.company || undefined,
		}


		try {
			const response = await (await pickupService.failPickup(valuesToFail)).getResponseData();
			if(response.success){
				toast.success('Recogida marcada como fallida con éxito');
			} else {
				handleErrors(response);
			}
			setLoading(false);
			handleClose();
		} catch (error) {
			console.error('❌ [FailPickup] Error:', error);
			toast.error('Error al marcar la recogida como fallida');
		} finally {
			setLoading(false);
		}
	};
	//-------------------------------------------------------------------------------------------------------------

	//-------------------------------------------------------------------------------------------------------------
	/**
	 * @ES CERRAR MODAL
	 * @EN CLOSE MODAL
	 */
	//-------------------------------------------------------------------------------------------------------------
	const handleClose = () => {
		formik.resetForm();
		onClose();
	};
	//-------------------------------------------------------------------------------------------------------------

	// USE EFFECT 

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

	// RENDER

	return (
		<ModalForm
			isOpen={isOpen}
			onClose={handleClose}
			title={`Marcar como Fallida - ${pickup.headquarter?.name || 'Sin sede'}`}
		>
			<form onSubmit={formik.handleSubmit} className="space-y-6">
				{/* Información de la recogida */}
				<div className="bg-gray-50 p-4 rounded-lg">
					<h3 className="text-sm font-medium text-gray-900 mb-2 font-bold">Información de la Recogida</h3>
					<div className="grid grid-cols-2 gap-4 text-sm">
						<div>
							<span className="font-medium">Sede:</span> {pickup.headquarter?.name || 'N/A'}
						</div>
						<div>
							<span className="font-medium">Estado:</span> {pickupStatuses.find(status => status.value === pickup?.pickupStatus)?.label || 'N/A'}
						</div>
						<div>
							<span className="font-medium">Ruta:</span> {pickup.route?.name || 'N/A'}
						</div>
					</div>
				</div>

				{/* Campos del formulario */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{/* Motivo */}
					<div>
						<RequiredInput
							id="failureReason"
							label="Motivo del fallo"
							placeholder="Ausencia de contacto..."
							formik={formik}
							error={formik.errors.failureReason}
						/>
					</div>

					{/* Fecha estimada para próxima recogida */}
					<div className='row-span-1 md:row-span-2'>
						<label htmlFor="newDate" className="block text-sm font-medium text-gray-900 mb-2">
							Programar próxima recogida <span className="text-red-600">*</span>
						</label>
						<DatePicker
							monthsShown={1}
							placeholderText="Selecciona una fecha y hora (DD/MM/YY HH:MM:SS)"
							showTimeSelect={true}
							timeIntervals={15}
							timeFormat="HH:mm"
							dateFormat="dd/MM/yyyy HH:mm"
							minDate={addDays(new Date(), 1)}
							inputProps={{
								inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.newDate ? 'border-red-500' : ''}`,
							}}
							className="col-span-12"
							selected={formik.values.newDate ? moment(formik.values.newDate, DATE_FORMATS.INTERNAL).toDate() : null}
							onChange={(date: Date | null) => {
								const internalFormat = formatDateFromPicker(date);
								formik.setFieldValue('newDate', internalFormat);
							}}
							locale={es}
						/>
						{formik.errors.newDate ? (
							<span className="text-xs text-red-600">
								{String(formik.errors.newDate)}
							</span>
						) : null}

						<p className="mt-2 text-xs text-amber-600">
							Nota: La fecha seleccionada podría no corresponder a la ruta habitual.
						</p>
					</div>

					{/* Usuario (opcional) */}
					<div>
						<CustomSelect
							id="userId"
							label="Usuario"
							placeholder="usuario"
							options={getUsersList()}
							value={getUsersList().find(user => user.value === formik.values.userId) || null}
							onChange={(value: any) => formik.setFieldValue('userId', value?.value || '')}
							error={formik.errors.userId}
							isClearable={true}
							isSearchable={true}
							isLoading={usersLoading}
							required={false}
						/>
					</div>
				</div>

				{/* Explicación del fallo */}
				<div>
					<label htmlFor="failureExplanation" className="block text-sm font-medium text-gray-900 mb-2">
						Explicación del fallo
					</label>
					<Textarea
						id="failureExplanation"
						placeholder="Describe la explicación del fallo..."
						rows={3}
						maxLength={1000}
						{...formik.getFieldProps('failureExplanation')}
					/>
				</div>

                {userCan('list', 'company') && (
					<CustomSelect
						required
						isSearchable
						id={'company'}
						label="Compañia"
						containerClassName='md:col-span-12'
						value={
							formik.values.company ?
							{ value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
							getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
						}
						options={getCompaniesList()}
						onChange={
							(e: any) => { formik.setFieldValue('company', e?.value) }
						}
						error={formik.errors.company}
					/>
				)}

				{/* Botones */}
				<div className="flex justify-end gap-3 py-4 border-t">
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={loading}
					>
						Cancelar
					</Button>
					<Button
						type="submit"
						isLoading={loading}
						disabled={
							!formik.values.failureReason ||
							!formik.values.newDate ||
							!formik.values.failureExplanation ||
							loading
						}
					>
						Marcar como Fallida
					</Button>
				</div>
			</form>
		</ModalForm>
	);
};

export default FailPickupModal;